import { Suspense, lazy } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import { ProductProvider } from 'src/contexts/ProductosContext';
import { CartProvider } from 'src/contexts/CartContext';
import SuspenseLoader from 'src/components/SuspenseLoader';

// ─── Lazy loader helper ───────────────────────────────────────────────────────
const Loader = (Component) => (props) => (
    <Suspense fallback={<SuspenseLoader />}>
        <Component {...props} />
    </Suspense>
);

// ─── Páginas públicas ─────────────────────────────────────────────────────────
const Login             = Loader(lazy(() => import('src/content/overview')));
const LoginTienda       = Loader(lazy(() => import('src/content/login-tienda')));
const Registro          = Loader(lazy(() => import('src/content/registro')));
const Presentacion      = Loader(lazy(() => import('src/content/presentacion')));
const Farmacovigilancia = Loader(lazy(() => import('src/content/farmacovigilancia')));
const Prueba            = Loader(lazy(() => import('src/content/presentacion/aurosant-mui.tsx')));

// ─── Dashboard (Admin) ────────────────────────────────────────────────────────
const Perfil              = Loader(lazy(() => import('src/content/dashboards/perfil')));
const Pedidos             = Loader(lazy(() => import('src/content/dashboards/pedidos')));
const Usuarios            = Loader(lazy(() => import('src/content/dashboards/usuarios')));
const Vendedores          = Loader(lazy(() => import('src/content/dashboards/vendedores')));
const Clientes            = Loader(lazy(() => import('src/content/dashboards/usuarios-tienda')));
const CategoriasClientes  = Loader(lazy(() => import('src/content/dashboards/categorias-clientes')));
const Productos           = Loader(lazy(() => import('src/content/dashboards/productos')));
const BloquePrecios       = Loader(lazy(() => import('src/content/dashboards/bloque_precios')));
const ListaPrecios        = Loader(lazy(() => import('src/content/dashboards/lista_precios')));
const Promociones         = Loader(lazy(() => import('src/content/dashboards/promociones')));
const Banners             = Loader(lazy(() => import('src/content/dashboards/banners')));
const BannersTienda       = Loader(lazy(() => import('src/content/dashboards/banners_tienda')));
const CategoriasPrincipal = Loader(lazy(() => import('src/content/dashboards/categorias_pagina')));
const BannersSecundarios  = Loader(lazy(() => import('src/content/dashboards/banners_secundarios')));
const ProductosDestacados = Loader(lazy(() => import('src/content/dashboards/productos_destacados')));

// ─── Tienda Online (Usuario) ──────────────────────────────────────────────────
const TiendaOnline       = Loader(lazy(() => import('src/content/tienda-online')));
const DetallesPedido     = Loader(lazy(() => import('src/content/tienda-online/CheckoutDialog')));
const ListaPedidos       = Loader(lazy(() => import('src/content/tienda-online/OrderHistory')));
const DetalleProducto    = Loader(lazy(() => import('src/content/tienda-online/ProductDetail')));
const ProductosFavoritos = Loader(lazy(() => import('src/content/tienda-online/FavoriteProducts')));
const PerfilUsuario      = Loader(lazy(() => import('src/content/tienda-online/UserProfile')));

// Recuperacion de contraseña
const SolicitarRecuperacion = Loader(lazy(() => import('src/content/solicitar-recuperacion')));
const RestablecerPassword   = Loader(lazy(() => import('src/content/restablecer-password')));

const Page404 = Loader(lazy(() => import('src/content/Page404')));


// ─── Guards ───────────────────────────────────────────────────────────────────

/**
 * Ruta pública: si el usuario ya está autenticado lo redirige a su área.
 * - ADMINISTRADOR → /dashboard/pedidos
 * - USUARIO       → /tienda
 */
const PublicRoute = ({ isLoggedIn, typeUser }) => {
    if (isLoggedIn && typeUser === 'ADMINISTRADOR') return <Navigate to="/dashboard/pedidos" replace />;
    if (isLoggedIn && typeUser === 'USUARIO')        return <Navigate to="/tienda" replace />;
    return <Outlet />;
};

/**
 * Ruta privada: solo accesible si el usuario tiene el rol requerido.
 * @param {string} requiredRole  - Rol necesario ('ADMINISTRADOR' | 'USUARIO')
 * @param {string} redirectTo    - A dónde redirigir si no cumple el rol
 */
const PrivateRoute = ({ isLoggedIn, typeUser, requiredRole, redirectTo }) => {
    if (!isLoggedIn || typeUser !== requiredRole) return <Navigate to={redirectTo} replace />;
    return <Outlet />;
};

/**
 * Wrapper que inyecta los providers de la Tienda sólo donde se necesitan.
 * Evita repetir <ProductProvider><CartProvider> en cada ruta.
 */
const TiendaProviders = () => (
    <ProductProvider>
        <CartProvider>
            <Outlet />
        </CartProvider>
    </ProductProvider>
);


// ─── Rutas ────────────────────────────────────────────────────────────────────
const routes = (isLoggedIn, typeUser) => [

    // ── Páginas completamente públicas (sin guard) ───────────────────────────
    {
        path: '/',
        element: <Presentacion />,
    },
    {
        path: '/prueba',
        element: <Prueba />,
    },
    {
        path: '/farmacovigilancia',
        element: <Farmacovigilancia />,
    },
    {
        path: '/recuperar-password',
        element: <SolicitarRecuperacion />,
    },
    {
        path: '/recuperar-password/:token',
        element: <RestablecerPassword />,
    },

    // ── Rutas solo para usuarios NO autenticados ─────────────────────────────
    {
        element: <PublicRoute isLoggedIn={isLoggedIn} typeUser={typeUser} />,
        children: [
            {
                element: <BaseLayout />,
                children: [
                    { path: '/administrador', element: <Login /> },
                    { path: '/login',         element: <LoginTienda /> },
                    { path: '/registro',      element: <Registro /> },
                ],
            },
        ],
    },

    // ── Rutas privadas: ADMINISTRADOR ────────────────────────────────────────
    {
        path: '/dashboard',
        element: (
            <PrivateRoute
                isLoggedIn={isLoggedIn}
                typeUser={typeUser}
                requiredRole="ADMINISTRADOR"
                redirectTo="/administrador"
            />
        ),
        children: [
            {
                element: <SidebarLayout />,
                children: [
                    { path: '',                  element: <Navigate to="pedidos" replace /> },
                    { path: 'perfil',            element: <Perfil /> },
                    { path: 'pedidos',           element: <Pedidos /> },
                    { path: 'usuarios',          element: <Usuarios /> },
                    { path: 'clientes',          element: <Clientes /> },
                    { path: 'vendedores',        element: <Vendedores /> },
                    { path: 'tipos-cliente',     element: <CategoriasClientes /> },
                    { path: 'productos',         element: <Productos /> },
                    { path: 'bloque-precios',    element: <BloquePrecios /> },
                    { path: 'lista-precios',     element: <ListaPrecios /> },
                    { path: 'promociones',       element: <Promociones /> },
                    { path: 'banners-principal', element: <Banners /> },
                    { path: 'banners-tienda',    element: <BannersTienda /> },
                    { path: 'categorias-principal', element: <CategoriasPrincipal /> },
                    { path: 'banners-secundarios',  element: <BannersSecundarios /> },
                    { path: 'productos-destacados', element: <ProductosDestacados /> },
                ],
            },
        ],
    },

    // ── Rutas privadas: USUARIO (Tienda Online) ──────────────────────────────
    {
        path: '/tienda',
        element: (
            <PrivateRoute
                isLoggedIn={isLoggedIn}
                typeUser={typeUser}
                requiredRole="USUARIO"
                redirectTo="/login"
            />
        ),
        children: [
            {
                // Todos los hijos comparten los mismos providers
                element: <TiendaProviders />,
                children: [
                    { path: '',                  element: <TiendaOnline /> },
                    { path: 'detalles-pedido',   element: <DetallesPedido /> },
                    { path: 'lista-pedidos',     element: <ListaPedidos /> },
                    { path: 'producto/:productId', element: <DetalleProducto /> },
                    { path: 'productos-favoritos', element: <ProductosFavoritos /> },
                    { path: 'mi-cuenta',         element: <PerfilUsuario /> },
                ],
            },
        ],
    },

    // ── Fallback global ──────────────────────────────────────────────────────
    {
        path: '*',
        element: <Page404 />,
    },
];

export default routes;